/**
 * HTTP Client for backend API calls
 * 
 * Provides a unified interface for making authenticated requests to the FastAPI backend.
 * Handles error normalization, auth token injection, and request/response logging.
 */

import { API_BASE_URL, generateCorrelationId, DEV_MODE } from '../config/env';

export interface ApiError {
    code: string;
    message: string;
    correlationId?: string;
    details?: any;
}

export interface ApiSuccess<T> {
    success: true;
    data: T;
}

export interface ApiFailure {
    success: false;
    error: ApiError;
}

export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

/**
 * HTTP client options
 */
export interface HttpOptions extends RequestInit {
    correlationId?: string;
}

/**
 * Make an authenticated HTTP request
 */
export async function httpRequest<T>(
    path: string,
    options: HttpOptions = {}
): Promise<ApiResult<T>> {
    const { correlationId = generateCorrelationId(), ...fetchOptions } = options;

    // Ensure proper URL construction - handle potential missing/extra slashes and backslashes
    const normalizedBase = API_BASE_URL.replace(/\/+$/, ''); // Remove trailing slashes
    const normalizedPath = path.replace(/\\/g, '/'); // Convert backslashes to forward slashes
    const finalPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`; // Ensure leading slash
    const url = `${normalizedBase}${finalPath}`;

    // Build headers
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Correlation-Id': correlationId,
        ...((fetchOptions.headers as Record<string, string>) || {})
    };

    // Remove Content-Type for FormData (let browser set it)
    if (fetchOptions.body instanceof FormData) {
        delete headers['Content-Type'];
    }

    const requestOptions: RequestInit = {
        ...fetchOptions,
        headers
    };

    // Making HTTP request

    try {
        const response = await fetch(url, requestOptions);
        const responseText = await response.text();

        let responseData: any = null;
        if (responseText) {
            try {
                responseData = JSON.parse(responseText);
            } catch {
                // Non-JSON response (e.g., plain text error)
                responseData = { message: responseText };
            }
        }

        if (!response.ok) {
            // Backend error format: { "error": { "code": "...", "message": "...", "correlation_id": "..." } }
            const errorData = responseData?.error || {};
            const apiError: ApiError = {
                code: errorData.code || `HTTP_${response.status}`,
                message: errorData.message || `Request failed with status ${response.status}`,
                correlationId: errorData.correlation_id || correlationId,
                details: errorData.details || responseData?.detail || responseData
            };

            // Request failed

            return { success: false, error: apiError };
        }

        // Request successful

        return { success: true, data: responseData };
    } catch (error) {
        const apiError: ApiError = {
            code: 'NETWORK_ERROR',
            message: error instanceof Error ? error.message : 'Network request failed',
            correlationId
        };

        // Network error occurred

        return { success: false, error: apiError };
    }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const httpClient = {
    get: <T>(path: string, options?: Omit<HttpOptions, 'method'>) =>
        httpRequest<T>(path, { ...options, method: 'GET' }),

    post: <T>(path: string, body?: any, options?: Omit<HttpOptions, 'method' | 'body'>) =>
        httpRequest<T>(path, {
            ...options,
            method: 'POST',
            body: body instanceof FormData ? body : JSON.stringify(body)
        }),

    patch: <T>(path: string, body?: any, options?: Omit<HttpOptions, 'method' | 'body'>) =>
        httpRequest<T>(path, {
            ...options,
            method: 'PATCH',
            body: body instanceof FormData ? body : JSON.stringify(body)
        }),

    put: <T>(path: string, body?: any, options?: Omit<HttpOptions, 'method' | 'body'>) =>
        httpRequest<T>(path, {
            ...options,
            method: 'PUT',
            body: body instanceof FormData ? body : JSON.stringify(body)
        }),

    delete: <T>(path: string, options?: Omit<HttpOptions, 'method'>) =>
        httpRequest<T>(path, { ...options, method: 'DELETE' })
};

/**
 * Unwrap successful API result or throw error
 * Useful for hooks that want to handle errors via try/catch
 */
export function unwrapApiResult<T>(result: ApiResult<T>): T {
    if (!result.success) {
        const failure = result as ApiFailure;
        const error = new Error(failure.error.message);
        (error as any).code = failure.error.code;
        (error as any).correlationId = failure.error.correlationId;
        (error as any).details = failure.error.details;
        throw error;
    }
    return result.data;
}